// Multi-select cities with type-and-Enter + suggestion chips.
//
// Value shape:  { items: string[] }
// Suggestion chips (`question.options`) toggle in/out on click.
// Text input + Enter (or the + button) adds a free-form entry.
// De-duped case-insensitively.
//
// Sunny 2026-08-13: was single-value; multi-select lets a coach target
// multiple cities/regions in one campaign (e.g. Jackie: China + HK + SG + TW + AU).
// Backend v2Campaigns.js reads `answers.where.items` as an array.
import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Group, Stack, Text, TextInput } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { SelectedChip, SuggestionChip, SuggestionsLabel } from '../components/WizardChips';

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

export default function LocationScreen({ question, value, onChange, hideHeader = false }) {
  const items = Array.isArray(value?.items) ? value.items : [];
  const [draft, setDraft] = useState('');
  const seededRef = useRef(false);

  // Seed once from question.default if the field is empty. Keeps the pre-v2.0.2
  // "text" default working for flows that specify one.
  useEffect(() => {
    if (seededRef.current) return;
    if (items.length > 0) { seededRef.current = true; return; }
    if (question.default) {
      seededRef.current = true;
      onChange({ items: [question.default] });
    } else {
      seededRef.current = true;
    }
  }, [items.length, question.default, onChange]);

  function addEntry(raw) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return;
    const key = normalize(trimmed);
    if (items.some((item) => normalize(item) === key)) {
      setDraft('');
      return;
    }
    onChange({ items: [...items, trimmed] });
    setDraft('');
  }

  function removeEntry(index) {
    const next = items.filter((_, i) => i !== index);
    onChange({ items: next });
  }

  function toggleOption(option) {
    const key = normalize(option);
    const existingIdx = items.findIndex((item) => normalize(item) === key);
    if (existingIdx >= 0) removeEntry(existingIdx);
    else addEntry(option);
  }

  const options = Array.isArray(question.options) ? question.options : [];
  const usedKeys = new Set(items.map(normalize));

  return (
    <Stack gap="lg">
      {!hideHeader && (
        <div>
          <Text fw={700} size="xl" style={{ color: 'var(--aviary-ink)', fontSize: 26, lineHeight: 1.3 }}>
            {question.ask}
          </Text>
          {question.sub && (
            <Text size="sm" mt={6} style={{ color: 'var(--aviary-muted)', lineHeight: 1.45 }}>
              {question.sub}
            </Text>
          )}
        </div>
      )}

      <Group gap="xs" align="flex-end" wrap="nowrap">
        <TextInput
          style={{ flex: 1 }}
          size="md"
          placeholder={question.placeholder || 'add a city or region…'}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addEntry(draft);
            }
          }}
        />
        <ActionIcon
          size={42}
          color="sage"
          variant="filled"
          onClick={() => addEntry(draft)}
          aria-label="Add"
        >
          <IconPlus size={20} />
        </ActionIcon>
      </Group>

      {items.length > 0 && (
        <Group gap="xs">
          {items.map((item, index) => (
            <SelectedChip
              key={`${normalize(item)}-${index}`}
              label={item}
              onRemove={() => removeEntry(index)}
            />
          ))}
        </Group>
      )}

      {options.length > 0 && (
        <Stack gap="xs">
          <SuggestionsLabel />
          <Group gap="xs">
            {options.map((option) => (
              <SuggestionChip
                key={option}
                label={option}
                used={usedKeys.has(normalize(option))}
                toggleable
                onClick={() => toggleOption(option)}
              />
            ))}
          </Group>
        </Stack>
      )}
    </Stack>
  );
}
